/**
 * Discovery management composable following SOLID principles
 * Single Responsibility: Handles discovery state and found items management
 */
import { ref, computed } from '@vue/composition-api';

/**
 * Composable for managing discovered/found items across game pages
 * @param {Object} options - Configuration options
 * @returns {Object} Discovery state and management methods
 */
export function useDiscovery() {
  // Discovery state management
  const hadFoundExhibitIds = ref([]);
  const hadFoundRelatedIds = ref([]);
  const hadFoundTimelineIds = ref([]);
  const hadFoundPositionIds = ref([]);
  const hadFoundSoulIds = ref([]);
  
  // Item type mapping for received items
  const translateItemToHadFound = {
    preExhId: 'hadFoundExhibitIds',
    preRelId: 'hadFoundRelatedIds', 
    preTimeId: 'hadFoundTimelineIds',
    prePosId: 'hadFoundPositionIds',
    preSoulId: 'hadFoundSoulIds'
  };
  
  // Current progress
  const progress = ref([]);
  
  /**
   * Initialize discovery data from API response
   * @param {Object} data - API response containing hadFound data
   */
  const initDiscoveryData = (data) => {
    if (!data || !data.hadFound) return;
    
    const hadFound = data.hadFound;
    hadFoundExhibitIds.value = hadFound.exhibitIds || [];
    hadFoundRelatedIds.value = hadFound.relatedIds || [];
    hadFoundTimelineIds.value = hadFound.timelineIds || [];
    hadFoundPositionIds.value = hadFound.positionIds || [];
    hadFoundSoulIds.value = hadFound.soulIds || [];
    
    progress.value = data.progress || [];
  };
  
  /**
   * Update found item list when new item is discovered
   * @param {Object} newItem - Newly discovered item
   * @param {string} itemType - Type of item (exhibit, related, etc.)
   * @param {Array} targetList - Target list to update (optional, will be inferred)
   */
  const updateFoundItem = (newItem, itemType, targetList = null) => {
    if (!newItem || !newItem.id || progress.value.includes(newItem.id)) {
      return false; // Item already found
    }
    
    // Add to progress
    progress.value.push(newItem.id);
    
    // Add to appropriate found list
    let foundList = targetList;
    if (!foundList) {
      switch (itemType) {
        case 'exhibit':
          foundList = hadFoundExhibitIds.value;
          break;
        case 'related':
          foundList = hadFoundRelatedIds.value;
          break;
        case 'timeline':
          foundList = hadFoundTimelineIds.value;
          break;
        case 'position':
          foundList = hadFoundPositionIds.value;
          break;
        case 'soul':
          foundList = hadFoundSoulIds.value;
          break;
        default:
          console.warn(`Unknown item type: ${itemType}`);
          return false;
      }
    }
    
    if (foundList && !foundList.includes(newItem.id)) {
      foundList.push(newItem.id);
    }
    
    return true; // Successfully updated
  };
  
  /**
   * Handle received items from event bus
   * @param {string} itemType - Type identifier (e.g., 'hadFoundExhibitIds')
   * @param {Object} item - Item object
   */
  const updateReceivedHadFoundItem = (itemType, item) => {
    if (!item || !item.id) return;
    
    // Get the appropriate array reference
    const foundListName = translateItemToHadFound[itemType] || itemType;
    
    switch (foundListName) {
      case 'hadFoundExhibitIds':
        if (!hadFoundExhibitIds.value.includes(item.id)) {
          hadFoundExhibitIds.value.push(item.id);
          updateFoundItem(item, 'exhibit', hadFoundExhibitIds.value);
        }
        break;
      case 'hadFoundRelatedIds':
        if (!hadFoundRelatedIds.value.includes(item.id)) {
          hadFoundRelatedIds.value.push(item.id);
          updateFoundItem(item, 'related', hadFoundRelatedIds.value);
        }
        break;
      case 'hadFoundTimelineIds':
        if (!hadFoundTimelineIds.value.includes(item.id)) {
          hadFoundTimelineIds.value.push(item.id);
          updateFoundItem(item, 'timeline', hadFoundTimelineIds.value);
        }
        break;
      case 'hadFoundPositionIds':
        if (!hadFoundPositionIds.value.includes(item.id)) {
          hadFoundPositionIds.value.push(item.id);
          updateFoundItem(item, 'position', hadFoundPositionIds.value);
        }
        break;
      case 'hadFoundSoulIds':
        if (!hadFoundSoulIds.value.includes(item.id)) {
          hadFoundSoulIds.value.push(item.id);
          updateFoundItem(item, 'soul', hadFoundSoulIds.value);
        }
        break;
    }
  };
  
  /**
   * Check if an item has been found
   * @param {number|string} itemId - Item ID to check
   * @param {string} itemType - Type of item
   * @returns {boolean} True if item has been found
   */
  const isItemFound = (itemId, itemType) => {
    switch (itemType) {
      case 'exhibit':
        return hadFoundExhibitIds.value.includes(itemId);
      case 'related':
        return hadFoundRelatedIds.value.includes(itemId);
      case 'timeline':
        return hadFoundTimelineIds.value.includes(itemId);
      case 'position':
        return hadFoundPositionIds.value.includes(itemId);
      case 'soul':
        return hadFoundSoulIds.value.includes(itemId);
      default:
        return progress.value.includes(itemId);
    }
  };
  
  /**
   * Get total count of found items by type
   * @param {string} itemType - Type of item
   * @returns {number} Count of found items
   */
  const getFoundItemCount = (itemType) => {
    switch (itemType) {
      case 'exhibit':
        return hadFoundExhibitIds.value.length;
      case 'related':
        return hadFoundRelatedIds.value.length;
      case 'timeline':
        return hadFoundTimelineIds.value.length;
      case 'position':
        return hadFoundPositionIds.value.length;
      case 'soul':
        return hadFoundSoulIds.value.length;
      default:
        return 0;
    }
  };
  
  /**
   * Process items and mark them as found or not
   * @param {Array} items - Array of items to process
   * @param {string} itemType - Type of items
   * @returns {Array} Processed items with found status
   */
  const processItemsWithFoundStatus = (items, itemType) => {
    if (!Array.isArray(items)) return [];
    
    return items.map(item => ({
      ...item,
      found: item.status === 'default' || isItemFound(item.id, itemType)
    }));
  };
  
  /**
   * Filter items by found status
   * @param {Array} items - Items to filter
   * @param {boolean} foundOnly - If true, return only found items
   * @returns {Array} Filtered items
   */
  const filterItemsByFoundStatus = (items, foundOnly = true) => {
    if (!Array.isArray(items)) return [];
    
    return items.filter(item => foundOnly ? item.found : !item.found);
  };
  
  // Computed properties for easy access
  const totalProgress = computed(() => progress.value.length);
  
  const discoveryStats = computed(() => ({
    exhibits: hadFoundExhibitIds.value.length,
    relateds: hadFoundRelatedIds.value.length,
    timelines: hadFoundTimelineIds.value.length,
    positions: hadFoundPositionIds.value.length,
    souls: hadFoundSoulIds.value.length,
    total: totalProgress.value
  }));
  
  return {
    // State
    hadFoundExhibitIds,
    hadFoundRelatedIds,
    hadFoundTimelineIds,
    hadFoundPositionIds,
    hadFoundSoulIds,
    progress,
    translateItemToHadFound,
    
    // Computed
    totalProgress,
    discoveryStats,
    
    // Methods
    initDiscoveryData,
    updateFoundItem,
    updateReceivedHadFoundItem,
    isItemFound,
    getFoundItemCount,
    processItemsWithFoundStatus,
    filterItemsByFoundStatus
  };
}